import { BEREICH_ARTEN } from './timeline.js';
/**
 * **KI-Schnitt** (7.66). Sascha, 3. November: „Wie könnten wir denn KI
 * Schnitt implementieren? Rohmaterial hochladen und schneiden automatisch.“
 *
 * Die Entscheidung ist ein Text-Problem: das Modell sieht nie ein Bild, nur
 * eine Liste von Abschnitten -- wer spricht, was gesagt wird, von wann bis
 * wann, in welcher Einstellung. Es antwortet mit Kennungen aus dieser Liste,
 * und nur mit denen. Was daraus Clips werden, rechnet die App selbst; ein
 * Modell, das Sekunden erfindet, kann so nichts kaputt schneiden.
 *
 * Hier liegt alles, was ohne Modell und ohne Netz geht: die Liste bauen, sie
 * als Text schreiben und die Antwort prüfen.
 */
/**
 * **Gespräch oder B-Roll** (Sascha, 4. November, gewählt: „automatisch
 * erkennen“). Der Arbeitsdienst misst beim Aufbereiten, welcher Anteil nach
 * Sprache aussieht; ab diesem Wert ist es ein Gespräch. Nicht gemessen
 * (`null`, Medien von davor) zählt als Gespräch -- lieber einmal zu viel
 * transkribiert als ein Interview übersehen.
 */
export const GESPRAECH_AB = 0.2;
export function istGespraech(sprachanteil, vonHand) {
    // Was jemand in den Medien gesagt hat, schlägt die Messung (4. November).
    if (vonHand)
        return vonHand === 'gespraech';
    return sprachanteil === null || sprachanteil === undefined || sprachanteil >= GESPRAECH_AB;
}
/** Die drei Vorlagen, die mitkommen. Bearbeitbar wie jede andere. */
export const KI_GRUNDVORLAGEN = [
    {
        name: 'Moderation säubern',
        daten: {
            art: 'ki',
            modus: 'herausnehmen',
            zielSekunden: null,
            anweisung: 'Das ist eine Moderation, in einem Stück aufgenommen. Nimm Versprecher, abgebrochene Sätze, ' +
                'doppelt angefangene Sätze (behalte den letzten, vollständigen Anlauf) und reine Füllwörter wie ' +
                '„äh“ und „ähm“ heraus. Alles andere bleibt, in der Reihenfolge, in der es gesagt wurde.',
        },
    },
    {
        name: 'Interview zu Beitrag',
        daten: {
            art: 'ki',
            modus: 'auswaehlen',
            zielSekunden: 90,
            anweisung: 'Das ist Rohmaterial aus einem Interview. Wähle die stärksten, verständlichsten Aussagen und ' +
                'bring sie in eine Reihenfolge, die eine Geschichte erzählt: ein Einstieg, der neugierig macht, ' +
                'der Kern, ein Schluss. Keine Fragen des Interviewers, keine halben Sätze.',
        },
    },
    {
        name: 'Langes zu Kurzvideo',
        daten: {
            art: 'ki',
            modus: 'auswaehlen',
            zielSekunden: 45,
            anweisung: 'Such aus dem Material den einen Moment, der allein für sich trägt -- eine Pointe, eine klare ' +
                'Aussage, etwas Überraschendes -- für ein Hochkant-Video in sozialen Netzen. Die ersten drei ' +
                'Sekunden müssen packen. Lieber kürzer als langatmig.',
        },
    },
];
/**
 * Die Liste der Abschnitte: ein Abschnitt je gesprochenem Satz des
 * Transkripts. Wechselt mitten im Satz die Einstellung, wird er dort geteilt,
 * an der Wortgrenze, die dem Wechsel am nächsten liegt -- sonst schnitte die
 * App später mitten durch eine Einstellung, ohne es zu wissen.
 */
export function abschnitteAus(quellen) {
    const raus = [];
    for (const q of quellen) {
        const schnitte = [...(q.schnitte ?? [])].sort((a, b) => a - b);
        const einstellungBei = (s) => 1 + schnitte.filter((x) => x <= s + 1e-6).length;
        for (const seg of q.transkript.segments) {
            const text = seg.text.trim();
            if (!text || !(seg.end > seg.start))
                continue;
            const innen = schnitte.filter((x) => x > seg.start + 0.05 && x < seg.end - 0.05);
            const worte = seg.words.filter((w) => w.end > w.start);
            if (innen.length === 0 || worte.length < 2) {
                raus.push({ id: '', medium: q.medium, von: seg.start, bis: seg.end, text, sprecher: seg.speaker, einstellung: einstellungBei(seg.start) });
                continue;
            }
            // An den Wortgrenzen teilen, die den Wechseln am nächsten liegen.
            let teil = [];
            let offen = [...innen];
            for (const w of worte) {
                if (offen.length && teil.length && w.start >= offen[0] - (w.end - w.start) / 2) {
                    raus.push(stueck(q, teil, seg.speaker, einstellungBei));
                    teil = [];
                    offen = offen.filter((x) => x > w.start);
                }
                teil.push(w);
            }
            if (teil.length)
                raus.push(stueck(q, teil, seg.speaker, einstellungBei));
        }
    }
    return raus.map((a, i) => ({ ...a, id: `a${i + 1}` }));
}
function stueck(q, worte, sprecher, einstellungBei) {
    const von = worte[0].start;
    return {
        id: '',
        medium: q.medium,
        von,
        bis: worte[worte.length - 1].end,
        text: worte.map((w) => w.text.trim()).filter(Boolean).join(' '),
        sprecher,
        einstellung: einstellungBei(von),
    };
}
/**
 * **Luft an den Schnitten** (Sascha, 4. November: „Er hat das erste Video zu
 * schnell abgeschnitten“). Ein Abschnitt reicht vom Anfang des ersten bis zum
 * Ende des letzten Wortes -- und Transkripte setzen das Ende knapp: der
 * Ausklang der letzten Silbe, der Atem danach, fiel weg. Jetzt bekommt jeder
 * Abschnitt etwas davor und danach, höchstens bis zur Mitte der Pause zum
 * Nachbarn in derselben Datei -- so rutscht kein fremdes Wort herein.
 */
export const LUFT_VOR = 0.15;
export const LUFT_NACH = 0.35;
export function mitLuft(a, alle, dauer) {
    let davor = -Infinity;
    let danach = Infinity;
    for (const b of alle) {
        if (b.medium !== a.medium || b.id === a.id)
            continue;
        if (b.bis <= a.von + 1e-6 && b.bis > davor)
            davor = b.bis;
        if (b.von >= a.bis - 1e-6 && b.von < danach)
            danach = b.von;
    }
    const vor = Math.min(LUFT_VOR, Number.isFinite(davor) ? (a.von - davor) / 2 : LUFT_VOR);
    const nach = Math.min(LUFT_NACH, Number.isFinite(danach) ? (danach - a.bis) / 2 : LUFT_NACH);
    const ende = dauer && dauer > 0 ? dauer : Infinity;
    return { luftVon: Math.max(0, a.von - Math.max(0, vor)), luftBis: Math.min(ende, a.bis + Math.max(0, nach)) };
}
/**
 * Aus den Stellen, die übernommen werden, die Clips: je Stelle einer, mit
 * Luft -- und Stellen, die in der Datei unmittelbar aufeinander folgen (a4,
 * a5), werden **ein** Clip. Dazwischen bliebe sonst ein Schnitt, der nichts
 * wegnimmt, nur die Pause zwischen zwei Sätzen zerhackt.
 */
export function kiClips(stellen) {
    const nummer = (id) => Number(/^a(\d+)$/.exec(id)?.[1] ?? NaN);
    const clips = [];
    for (const s of stellen) {
        const teile = s.teile?.length ? s.teile : [{ von: s.luftVon ?? s.von, bis: s.luftBis ?? s.bis }];
        teile.forEach((t, i) => {
            const vorher = clips.at(-1);
            // Nur das erste Stück einer Stelle schliesst an die vorige an; die
            // weiteren sind gerade deshalb eigene Clips, weil dazwischen etwas fehlt.
            if (i === 0 && vorher && vorher.medium === s.medium && nummer(s.id) === vorher.letzte + 1 && t.von <= vorher.bis + 0.5) {
                vorher.bis = Math.max(vorher.bis, t.bis);
                vorher.text = `${vorher.text} ${s.text}`;
                vorher.ids.push(s.id);
                vorher.letzte = nummer(s.id);
                return;
            }
            clips.push({ medium: s.medium, von: t.von, bis: t.bis, text: s.text, ids: [s.id], letzte: nummer(s.id) });
        });
    }
    return clips.map(({ letzte: _letzte, ...c }) => c);
}
/**
 * **Füllwörter und Pausen** (Sascha, 4. November: „Füllwörter & Pausen
 * raus“). Rein aus den Wortzeiten, ohne Modell: was zwischen `von` und `bis`
 * bleibt, wenn „äh“, „ähm“ und Verwandte herausfallen und jede Pause über
 * `maxPause` auf einen kurzen Atem schrumpft. Zurück die Stücke, die bleiben.
 */
export const FUELLWOERTER = ['äh', 'ähm', 'äähm', 'ääh', 'öh', 'öhm', 'hm', 'hmm', 'mhm', 'ehm', 'eh', 'uh', 'uhm', 'em'];
export function straffeTeile(worte, von, bis, opts = {}) {
    const maxPause = opts.maxPause ?? 0.5;
    const atem = opts.atem ?? 0.25;
    const minStueck = opts.minStueck ?? 0.15;
    const fuell = new Set(FUELLWOERTER);
    const drin = worte.filter((w) => w.start >= von - 0.05 && w.end <= bis + 0.05 && w.end > w.start).sort((x, y) => x.start - y.start);
    const echte = drin.filter((w) => !fuell.has(wortForm(w.text)));
    if (echte.length === 0)
        return [{ von, bis }];
    // Was herausfällt: von nach dem letzten echten Wort bis vor das nächste,
    // wenn dazwischen ein Füllwort steht oder die Pause zu lang ist.
    const weg = [];
    for (let i = 0; i + 1 < echte.length; i++) {
        const a = echte[i];
        const b = echte[i + 1];
        const fuellDazwischen = drin.some((w) => w.start >= a.end - 1e-6 && w.end <= b.start + 1e-6 && fuell.has(wortForm(w.text)));
        const pause = b.start - a.end;
        if (fuellDazwischen || pause > maxPause) {
            const halb = Math.min(atem, pause) / 2;
            weg.push({ von: a.end + halb, bis: b.start - halb });
        }
    }
    // Ein Füllwort vor dem ersten oder nach dem letzten echten Wort: die
    // Stelle beginnt später oder endet früher.
    let anfang = von;
    let ende = bis;
    if (drin[0] && fuell.has(wortForm(drin[0].text)))
        anfang = Math.max(von, echte[0].start - Math.min(0.15, atem));
    const letztes = drin.at(-1);
    if (letztes && fuell.has(wortForm(letztes.text)))
        ende = Math.min(bis, echte.at(-1).end + Math.min(0.35, atem));
    const raus = [];
    let t = anfang;
    for (const w of weg) {
        if (w.bis <= w.von)
            continue;
        if (w.von - t >= minStueck)
            raus.push({ von: t, bis: w.von });
        t = Math.max(t, w.bis);
    }
    if (ende - t >= minStueck)
        raus.push({ von: t, bis: ende });
    return raus.length ? raus : [{ von, bis }];
}
/** Sekunden als „1:05.3“ -- kurz, für das Modell lesbar. */
function zeit(s) {
    const m = Math.floor(s / 60);
    return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
}
/**
 * Die Liste als Text für das Modell: eine Zeile je Abschnitt. Kein JSON
 * hinein -- Zeilen sind kürzer, und ein kleines Modell liest sie besser.
 */
export function abschnittsText(liste, namen = {}) {
    return liste
        .map((a) => {
        const wer = a.sprecher ? ` · ${a.sprecher}` : '';
        const wo = namen[a.medium] ?? a.medium;
        return `${a.id} | ${wo} E${a.einstellung} ${zeit(a.von)}–${zeit(a.bis)} (${(a.bis - a.von).toFixed(1)} s)${wer} | ${a.text}`;
    })
        .join('\n');
}
/** Die Nachricht an das Modell: Vorlage, Briefing, Liste und das Antwortformat. */
export function kiAuftrag(vorlage, briefing, liste, namen = {}, 
/** Ob Standbilder mitgehen (4. November) -- dann sagt der Auftrag, wozu. */
mitBildern = false) {
    const regeln = [
        'Du schneidest Video nach einem Transkript. Du siehst nur die Abschnitte unten, jede Zeile beginnt mit ihrer Kennung.',
        'Antworte ausschliesslich mit JSON in dieser Form: {"abschnitte": ["a3", "a7", ...], "begruendung": "ein Satz"}.',
        'Verwende nur Kennungen aus der Liste, jede höchstens einmal.',
        vorlage.modus === 'herausnehmen'
            ? 'Behalte die Reihenfolge der Liste bei; du darfst nur weglassen, nicht umstellen.'
            : 'Du darfst auswählen und die Reihenfolge frei bestimmen.',
        // Hinweise und Bereiche (4. November): das Modell darf mehr als
        // auswählen -- es sagt, wo jemand von Hand nacharbeiten sollte, und teilt
        // den Schnitt in Teile, wenn mehrere verlangt sind.
        'Zusätzlich, wenn es hilft: "hinweise": [{"bei": "a3", "text": "was dort zu tun ist"}] -- höchstens 8, nur an Abschnitten, die du gewählt hast, ' +
            'und nur Handfestes: wo B-Roll hingehört, ein Versprecher zu prüfen ist, eine Bauchbinde mit Namen passt, ein Übergang hakt.',
        'Und nur, wenn Auftrag oder Briefing mehrere Teile verlangen (Storys, Teaser, Reel): "bereiche": [{"name": "Story 1", "von": "a3", "bis": "a7", "art": "story"}] -- ' +
            'von und bis sind gewählte Abschnitte, ein Bereich reicht in deiner Reihenfolge vom einen bis zum anderen; art ist story, reel, teaser oder video.',
        // Effekte (4. November): Übergänge und Bauchbinden -- sparsam.
        'Übergänge: "uebergaenge": [{"vor": "a5", "art": "blende"}] -- nur wo ein harter Schnitt stört (Themenwechsel, Sprung in Zeit oder Ort); ' +
            'art ist blende, schwarz oder weiss. Ohne Angabe bleibt es ein harter Schnitt.',
        'Bauchbinden: "bauchbinden": [{"bei": "a1", "name": "Anna Berg", "rolle": "Organisatorin"}] -- beim ersten Auftritt einer Person, ' +
            'nur mit Namen, die wörtlich im Material stehen; sonst keine.',
        // Schlagwörter für die Untertitel (4. November).
        'Schlagwörter für die Untertitel: "schlagwoerter": [{"bei": "a3", "wort": "Laternenumzug"}] -- was im Untertitel hervorstechen soll ' +
            '(Kernbegriff, Zahl, Name, Pointe), wörtlich aus dem Abschnitt, höchstens eines je Abschnitt.',
    ];
    if (mitBildern) {
        // Zoom auf die sprechende Person (Sascha, 4. November: „so kannst du
        // besser zoom verwenden (keyframes) auf die sprechende person“).
        regeln.push('Zoom: "zooms": [{"bei": "a3", "x": 0.62, "y": 0.35, "staerke": 1.25, "art": "sanft"}] -- x und y sind die Mitte des Gesichts der sprechenden Person ' +
            'im Standbild der Einstellung (0 bis 1, von links oben), staerke 1.05 bis 1.6; art „sanft“ fährt während der Stelle langsam heran, ' +
            '„hart“ springt für eine Betonung näher. Sparsam: an starken Aussagen, am Schluss, gegen lange gleiche Einstellungen. ' +
            'Für Schlagwörter -- Kernbegriffe, Zahlen, Namen, die Pointe -- gib "wort": "Laternenumzug" dazu (wörtlich aus dem Abschnitt): ' +
            'dann springt der Zoom genau auf dieses Wort.');
    }
    if (vorlage.zielSekunden)
        regeln.push(`Das Ergebnis soll ungefähr ${Math.round(vorlage.zielSekunden)} Sekunden lang sein.`);
    if (mitBildern) {
        regeln.push('Zu den Einstellungen siehst du Standbilder, beschriftet mit Datei und Einstellung (E1, E2, …) wie in der Liste. ' +
            'Meide Einstellungen, die unscharf, verwackelt, über- oder unterbelichtet sind, in denen die Person angeschnitten ist oder die Augen zu hat; ' +
            'sagt jemand denselben Satz mehrmals, nimm den Anlauf, der auch im Bild besser ist. ' +
            'Hat eine gewählte Einstellung im Bild ein Problem, schreib einen Hinweis dazu.');
    }
    const nutzer = [
        `Auftrag: ${vorlage.anweisung}`,
        briefing.trim() ? `Briefing: ${briefing.trim()}` : '',
        '',
        'Abschnitte:',
        abschnittsText(liste, namen),
    ]
        .filter((z, i) => z !== '' || i === 2)
        .join('\n');
    return { system: regeln.join('\n'), nutzer };
}
/**
 * Die Antwort des Modells prüfen. Ein Modell darf sich irren, die App nicht:
 * unbekannte Kennungen und doppelte fallen heraus, und im Modus
 * „herausnehmen“ zählt die Reihenfolge der Liste, egal was zurückkam.
 * Gibt `null` zurück, wenn gar nichts Brauchbares darin steht.
 */
export function pruefeAntwort(roh, liste, modus) {
    const json = jsonAus(roh);
    if (!json || typeof json !== 'object')
        return null;
    const ids = json.abschnitte;
    if (!Array.isArray(ids))
        return null;
    const nachId = new Map(liste.map((a, i) => [a.id, { a, i }]));
    const gesehen = new Set();
    const verworfen = [];
    const gewaehlt = [];
    for (const roheId of ids) {
        const id = String(roheId).trim();
        const treffer = nachId.get(id);
        if (!treffer) {
            verworfen.push(`${id}: gibt es nicht`);
            continue;
        }
        if (gesehen.has(id)) {
            verworfen.push(`${id}: doppelt`);
            continue;
        }
        gesehen.add(id);
        gewaehlt.push(treffer);
    }
    if (gewaehlt.length === 0)
        return null;
    if (modus === 'herausnehmen')
        gewaehlt.sort((x, y) => x.i - y.i);
    const abschnitte = gewaehlt.map((g) => g.a);
    const begruendung = String(json.begruendung ?? '').slice(0, 500);
    const platz = new Map(abschnitte.map((a, i) => [a.id, i]));
    // Hinweise: nur an gewählten Abschnitten, mit Text.
    const hinweise = [];
    const roheHinweise = json.hinweise;
    for (const h of Array.isArray(roheHinweise) ? roheHinweise : []) {
        const bei = String(h?.bei ?? '').trim();
        const text = String(h?.text ?? '').replace(/\s+/g, ' ').trim().slice(0, 300);
        if (!text)
            continue;
        if (!platz.has(bei)) {
            verworfen.push(`Hinweis bei ${bei || '?'}: kein gewählter Abschnitt`);
            continue;
        }
        if (hinweise.length < 12)
            hinweise.push({ bei, text });
    }
    hinweise.sort((x, y) => platz.get(x.bei) - platz.get(y.bei));
    // Bereiche: von und bis gewählt; verkehrt herum wird umgedreht.
    const bereiche = [];
    const roheBereiche = json.bereiche;
    for (const b of Array.isArray(roheBereiche) ? roheBereiche : []) {
        let von = String(b?.von ?? '').trim();
        let bis = String(b?.bis ?? '').trim() || von;
        if (!platz.has(von) || !platz.has(bis)) {
            verworfen.push(`Bereich ${von || '?'}–${bis || '?'}: kein gewählter Abschnitt`);
            continue;
        }
        if (platz.get(von) > platz.get(bis))
            [von, bis] = [bis, von];
        const art = String(b?.art ?? '');
        const name = String(b?.name ?? '').replace(/\s+/g, ' ').trim().slice(0, 60) || `Teil ${bereiche.length + 1}`;
        if (bereiche.length < 10)
            bereiche.push({ name, von, bis, art: BEREICH_ARTEN.includes(art) ? art : null });
    }
    // Übergänge: vor einer gewählten Stelle, nicht vor der ersten.
    const uebergaenge = [];
    const roheUebergaenge = json.uebergaenge;
    for (const u of Array.isArray(roheUebergaenge) ? roheUebergaenge : []) {
        const vor = String(u?.vor ?? '').trim();
        const art = String(u?.art ?? 'blende').trim();
        if (!platz.has(vor) || platz.get(vor) === 0) {
            verworfen.push(`Übergang vor ${vor || '?'}: keine gewählte Stelle mit einer davor`);
            continue;
        }
        if (art !== 'blende' && art !== 'schwarz' && art !== 'weiss')
            continue;
        if (!uebergaenge.some((x) => x.vor === vor))
            uebergaenge.push({ vor, art });
    }
    uebergaenge.sort((x, y) => platz.get(x.vor) - platz.get(y.vor));
    // Bauchbinden: nur Namen, die wirklich im Material stehen -- ein Modell,
    // das sich einen Namen ausdenkt, schreibt ihn sonst ins Bild.
    const material = liste.map((a) => a.text).join(' ').toLocaleLowerCase('de');
    const bauchbinden = [];
    const roheBinden = json.bauchbinden;
    for (const b of Array.isArray(roheBinden) ? roheBinden : []) {
        const bei = String(b?.bei ?? '').trim();
        const name = String(b?.name ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);
        const rolle = String(b?.rolle ?? '').replace(/\s+/g, ' ').trim().slice(0, 80);
        if (!name)
            continue;
        if (!platz.has(bei)) {
            verworfen.push(`Bauchbinde bei ${bei || '?'}: kein gewählter Abschnitt`);
            continue;
        }
        if (!material.includes(name.toLocaleLowerCase('de'))) {
            verworfen.push(`Bauchbinde „${name}“: der Name steht nicht im Material`);
            continue;
        }
        if (bauchbinden.length < 6 && !bauchbinden.some((x) => x.name === name))
            bauchbinden.push({ bei, name, rolle });
    }
    bauchbinden.sort((x, y) => platz.get(x.bei) - platz.get(y.bei));
    // Zooms: an gewählten Stellen, mit einer Stelle im Bild; die Stärke in
    // vernünftigen Grenzen -- über 1,6 sieht man die Pixel.
    const zooms = [];
    const roheZooms = json.zooms;
    for (const z of Array.isArray(roheZooms) ? roheZooms : []) {
        const bei = String(z?.bei ?? '').trim();
        const x = Number(z?.x);
        const y = Number(z?.y);
        const staerke = Number(z?.staerke ?? 1.2);
        const art = z?.art === 'hart' ? 'hart' : 'sanft';
        if (!platz.has(bei)) {
            verworfen.push(`Zoom bei ${bei || '?'}: kein gewählter Abschnitt`);
            continue;
        }
        if (!(x >= 0 && x <= 1 && y >= 0 && y <= 1) || !Number.isFinite(staerke)) {
            verworfen.push(`Zoom bei ${bei}: keine Stelle im Bild`);
            continue;
        }
        const wort = String(z?.wort ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);
        if (!zooms.some((q) => q.bei === bei && (q.wort ?? '') === wort)) {
            zooms.push({ bei, x, y, staerke: Math.min(1.6, Math.max(1.05, staerke)), art: wort ? 'hart' : art, ...(wort ? { wort } : {}) });
        }
    }
    zooms.sort((p, q) => platz.get(p.bei) - platz.get(q.bei));
    const schlagwoerter = [];
    const roheWoerter = json.schlagwoerter;
    for (const w of Array.isArray(roheWoerter) ? roheWoerter : []) {
        const bei = String(w?.bei ?? '').trim();
        const wort = String(w?.wort ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);
        if (!wort)
            continue;
        if (!platz.has(bei)) {
            verworfen.push(`Schlagwort bei ${bei || '?'}: kein gewählter Abschnitt`);
            continue;
        }
        if (schlagwoerter.length < 30 && !schlagwoerter.some((x) => x.bei === bei && x.wort === wort))
            schlagwoerter.push({ bei, wort });
    }
    return { abschnitte, begruendung, dauer: abschnitte.reduce((s, a) => s + (a.bis - a.von), 0), verworfen, hinweise, bereiche, uebergaenge, bauchbinden, zooms, schlagwoerter };
}
/** Ein Wort ohne Satzzeichen und Gross/klein -- „Umzug.“ und „umzug“ sind dasselbe. */
function wortForm(w) {
    return w.toLocaleLowerCase('de').replace(/[^\p{L}\p{N}]+/gu, '');
}
/**
 * **Wo ein Schlagwort gesagt wird** (Sascha, 4. November: „es gibt doch das,
 * dass die Schlagwörter und wichtige Dinge gezoomt werden“). Gesucht wird in
 * den Wörtern des Transkripts zwischen `von` und `bis`; ein Schlagwort aus
 * mehreren Wörtern („300 Kinder“) muss in dieser Folge dastehen. Zurück die
 * Zeit in der Datei, oder `null`, wenn es dort nicht gesagt wird.
 */
export function wortZeit(transkript, von, bis, wort) {
    const gesucht = wort.split(/\s+/).map(wortForm).filter(Boolean);
    if (gesucht.length === 0)
        return null;
    const worte = transkript.segments
        .flatMap((seg) => seg.words)
        .filter((w) => w.start >= von - 0.05 && w.end <= bis + 0.05 && wortForm(w.text) !== '');
    for (let i = 0; i + gesucht.length <= worte.length; i++) {
        if (gesucht.every((g, j) => wortForm(worte[i + j].text) === g)) {
            return { von: worte[i].start, bis: worte[i + gesucht.length - 1].end };
        }
    }
    return null;
}
/**
 * Wohin die Ebene rückt, damit ein Punkt im Bild bei dieser Vergrösserung an
 * seiner Stelle bleibt -- und so, dass das Bild die Leinwand weiter füllt
 * (keine schwarzen Ränder). Gerechnet für ein Bild, das die Leinwand füllt.
 */
export function zoomAuf(x, y, staerke) {
    const s = Math.max(1, staerke);
    const halb = s / 2;
    const klemme = (v) => Math.min(halb, Math.max(1 - halb, v));
    return { x: klemme(x + s * (0.5 - x)), y: klemme(y + s * (0.5 - y)), scale: s };
}
/**
 * Die Keyframes eines Zooms in einem Clip: „sanft“ fährt von der vollen
 * Einstellung über die Stelle heran, „hart“ springt am Anfang der Stelle
 * näher und geht an ihrem Ende zurück. Zeiten in Sekunden im Clip.
 */
export function zoomKeyframes(zoom, von, bis, clipLaenge) {
    const voll = { x: 0.5, y: 0.5, scale: 1, rotate: 0, opacity: 1 };
    const nah = { ...voll, ...zoomAuf(zoom.x, zoom.y, zoom.staerke) };
    const a = Math.max(0, Math.min(von, clipLaenge));
    const b = Math.max(a, Math.min(bis, clipLaenge));
    if (zoom.art === 'sanft') {
        return [
            { at: a, transform: voll, kurve: 'weich' },
            { at: b, transform: nah, kurve: 'weich' },
        ];
    }
    // Hart: ein Sprung -- zwei Keyframes eine Bildlänge auseinander genügen,
    // damit dazwischen nichts gleitet.
    const schritt = 0.04;
    const raus = [];
    if (a > 0)
        raus.push({ at: Math.max(0, a - schritt), transform: voll, kurve: 'linear' });
    raus.push({ at: a, transform: nah, kurve: 'linear' });
    if (b < clipLaenge - schritt) {
        raus.push({ at: Math.max(a, b - schritt), transform: nah, kurve: 'linear' });
        raus.push({ at: b, transform: voll, kurve: 'linear' });
    }
    return raus;
}
/** Was ein Übergang des Modells auf der Zeitleiste heisst. */
export const KI_UEBERGANG_ART = { blende: 'crossfade', schwarz: 'dipToBlack', weiss: 'dipToWhite' };
/**
 * Welcher Clip einen Übergang bekommt: der, der mit dieser Stelle **beginnt**
 * -- und nicht der erste. Liegt die Stelle mitten in einem zusammengelegten
 * Clip, gibt es dort keinen Schnitt, also auch keinen Übergang.
 */
export function kiUebergaengeAnClips(clips, uebergaenge) {
    const raus = new Map();
    for (const u of uebergaenge) {
        const i = clips.findIndex((c) => c.ids[0] === u.vor);
        if (i > 0)
            raus.set(i, u);
    }
    return raus;
}
/**
 * Wo jede übernommene Stelle auf der Zeitleiste landet -- für Hinweise und
 * Bereiche (4. November). Die Clips liegen lückenlos ab `ab`; eine Stelle in
 * einem zusammengelegten Clip beginnt dort, wo sie in der Datei beginnt.
 */
export function kiPositionen(clips, stellen, ab = 0) {
    const nachId = new Map(stellen.map((s) => [s.id, s]));
    const raus = new Map();
    let t = ab;
    clips.forEach((c, i) => {
        for (const id of c.ids) {
            const s = nachId.get(id);
            if (!s)
                continue;
            const von = Math.max(c.von, s.luftVon ?? s.von);
            const bis = Math.min(c.bis, s.luftBis ?? s.bis);
            const da = raus.get(id);
            // Ist eine Stelle auf mehrere Clips verteilt (Pausen heraus), beginnt
            // sie im ersten und endet im letzten.
            if (da)
                da.ende = t + (bis - c.von);
            else
                raus.set(id, { start: t + (von - c.von), ende: t + (bis - c.von), clip: i });
        }
        t += c.bis - c.von;
    });
    return raus;
}
/**
 * **Untertitel für den KI-Schnitt** (Sascha, 4. November: „Untertitel mit
 * Schlagwörtern“). Die Untertitel der App hängen an *einer* Abschrift mit
 * einer Zeit; der KI-Schnitt mischt Stücke aus mehreren Dateien. Darum
 * entsteht hier eine eigene Abschrift in der Zeit des Schnitts: die Wörter
 * jedes Clips, auf seine Stelle im Schnitt gerückt, Füllwörter und was
 * herausgeschnitten ist ausgelassen -- in kurzen Zeilen, wie sie in Reels
 * stehen, und mit den Schlagwörtern als betont.
 */
export function kiUntertitel(clips, worte, schlagwoerter, opts = {}) {
    const maxWoerter = opts.maxWoerter ?? 4;
    const maxZeichen = opts.maxZeichen ?? 22;
    const fuell = new Set(FUELLWOERTER);
    const imSchnitt = [];
    let t = 0;
    for (const c of clips) {
        for (const w of worte[c.medium] ?? []) {
            if (w.start < c.von - 0.02 || w.end > c.bis + 0.02 || fuell.has(wortForm(w.text)) || !w.text.trim())
                continue;
            const betont = schlagwoerter.some((k) => k.medium === c.medium && w.start >= k.von - 0.02 && w.end <= k.bis + 0.02);
            imSchnitt.push({ start: t + Math.max(0, w.start - c.von), end: t + Math.min(c.bis, w.end) - c.von, text: w.text.trim(), betont });
        }
        t += c.bis - c.von;
    }
    const segments = [];
    let zeile = [];
    const fertig = () => {
        if (zeile.length === 0)
            return;
        const betont = zeile.filter((w) => w.betont).map((w) => w.text);
        segments.push({
            id: segments.length,
            start: zeile[0].start,
            end: zeile.at(-1).end,
            text: zeile.map((w) => w.text).join(' '),
            speaker: null,
            words: zeile.map(({ betont: _b, ...w }) => w),
            ...(betont.length ? { betont } : {}),
        });
        zeile = [];
    };
    for (const w of imSchnitt) {
        const laenge = zeile.map((x) => x.text).join(' ').length + w.text.length + 1;
        const vorher = zeile.at(-1);
        if (vorher && (zeile.length >= maxWoerter || laenge > maxZeichen || w.start - vorher.end > 0.4 || /[.!?…]$/.test(vorher.text)))
            fertig();
        zeile.push(w);
    }
    fertig();
    // Lücken unter 0,3 s schliessen: sonst blinkt der Untertitel zwischen zwei Zeilen.
    for (let i = 0; i + 1 < segments.length; i++) {
        if (segments[i + 1].start - segments[i].end < 0.3)
            segments[i].end = segments[i + 1].start;
    }
    return { language: opts.sprache ?? 'de', offset: 0, segments };
}
/**
 * Wo eine Sekunde einer Datei im Schnitt liegt -- für alles, was der Server
 * in Dateizeit rechnet (Schlagwörter). Fällt sie in etwas Herausgenommenes,
 * gilt der Anfang des nächsten Stücks; liegt sie in keinem Clip, `null`.
 */
export function zeitImSchnitt(clips, medium, sekunde, ab = 0) {
    let t = ab;
    let danach = null;
    for (const [i, c] of clips.entries()) {
        if (c.medium === medium) {
            if (sekunde >= c.von - 1e-6 && sekunde <= c.bis + 1e-6)
                return { zeit: t + Math.max(0, sekunde - c.von), clip: i };
            if (!danach && c.von > sekunde && c.von - sekunde < 2)
                danach = { zeit: t, clip: i };
        }
        t += c.bis - c.von;
    }
    return danach;
}
/** JSON aus einer Modell-Antwort holen -- auch wenn es in ```json … ``` steht oder Text drumherum hat. */
function jsonAus(roh) {
    const ohneZaun = roh.replace(/```(?:json)?/gi, '');
    const anfang = ohneZaun.indexOf('{');
    const ende = ohneZaun.lastIndexOf('}');
    if (anfang < 0 || ende <= anfang)
        return null;
    try {
        return JSON.parse(ohneZaun.slice(anfang, ende + 1));
    }
    catch {
        return null;
    }
}
/**
 * **Nachschärfen** (7.66): „kürzer“, „anderer Einstieg“. Das Modell baut auf
 * seinem letzten Vorschlag auf statt von vorn -- es bekommt ihn mit, und dazu,
 * was daran nicht passte. Was jemand von Hand abgewählt hat, steht nicht mehr
 * in diesem Vorschlag; auch das ist eine Rückmeldung.
 */
export function nachschaerfen(auftrag, vorher, rueckmeldung) {
    const was = rueckmeldung.trim();
    return {
        system: auftrag.system,
        nutzer: [
            auftrag.nutzer,
            '',
            `Dein letzter Vorschlag (so, wie er jetzt steht): ${vorher.length ? vorher.join(', ') : '(leer)'}`,
            was ? `Rückmeldung dazu: ${was}` : 'Rückmeldung dazu: noch einmal, besser.',
            'Mach einen neuen Vorschlag, der die Rückmeldung umsetzt. Gleiches Antwortformat.',
        ].join('\n'),
    };
}
//# sourceMappingURL=kischnitt.js.map